import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { StlProviders } from '../txTrackerConsts'
import { TreatmentTypes } from 'consts/treatmentConsts'
import useScans from 'hooks/useScans'
import { isRetentionStamp, parseTxTrackerData, readTrackingStamp } from '../trackingStampUtils'

export default ({ grinScanId }) => {
  const { getScanNumberByTimelineItemData } = useScans()

  const patientGrinScans = useSelector(state => state.patientsReducer.patient?.grinScans?.items || [])
  const { treatment } = useSelector(state => state.treatmentReducer)

  const grinScan = useMemo(() => patientGrinScans.find(scan => scan.id === grinScanId), [grinScanId, patientGrinScans])
  const scanNumber = useMemo(
    () => getScanNumberByTimelineItemData(grinScan?.id),
    [grinScan, getScanNumberByTimelineItemData]
  )
  const parsedTxTrackerData = useMemo(
    () => parseTxTrackerData(grinScan?.treatmentTrackerData),
    [grinScan?.treatmentTrackerData]
  )
  // Deliberately not flag-gated. The retention feature flag gates the entry points and new
  // work; how an existing result is read is decided by the stamp on the scan. Gating here
  // would not hide a retention result, it would render one as an active result: the active
  // plan's origin models, the wrong labels, and a history grouped with scans it was never
  // measured against.
  const trackingStamp = useMemo(() => readTrackingStamp(grinScan?.treatmentTrackerData), [grinScan])
  const isRetentionResult = isRetentionStamp(trackingStamp)

  const calculatedTreatmentType = useMemo(() => {
    if (!treatment) {
      return null
    }

    // Retention runs are dispatched as a Braces flow against the reference set, whatever
    // the treatment's own type is.
    if (isRetentionResult) {
      return TreatmentTypes.Braces
    }

    const { provider } = JSON.parse(treatment.txTrackerStls || '{}')
    return provider === StlProviders.Align ||
      provider === StlProviders.Angel ||
      provider === StlProviders.AlignersOtherStartEnd
      ? TreatmentTypes.Braces
      : treatment.type
  }, [treatment, isRetentionResult])

  const parsedStls = useMemo(() => JSON.parse(treatment?.txTrackerStls || '{}'), [treatment?.txTrackerStls])
  const archModels = useMemo(() => {
    // A retention result carries all its models itself; the active plan's txTrackerStls
    // may not even exist for a retention-only patient.
    if (!!parsedStls?.stls || isRetentionResult) {
      return {
        baseArchModels: {
          upper: parsedTxTrackerData?.archModels?.upperS3Keys?.baseStl,
          lower: parsedTxTrackerData?.archModels?.lowerS3Keys?.baseStl
        },
        endArchModels: {
          upper: parsedTxTrackerData?.archModels?.upperS3Keys?.finalStl,
          lower: parsedTxTrackerData?.archModels?.lowerS3Keys?.finalStl
        },
        currentArchModels: {
          upper: parsedTxTrackerData?.archModels?.upperS3Keys?.currentPlannedStl,
          lower: parsedTxTrackerData?.archModels?.lowerS3Keys?.currentPlannedStl
        }
      }
    }

    return null
  }, [parsedStls, parsedTxTrackerData, isRetentionResult])
  const alignerNumber = useMemo(() => (grinScan?.withAligner ? grinScan.applianceIndex : null), [grinScan])
  const initAlignerNumber = useMemo(() => Object.keys(parsedStls?.stls?.lower || {})?.[0], [parsedStls])
  const endAlignerNumber = useMemo(() => Object.keys(parsedStls?.stls?.lower || {})?.pop(), [parsedStls])

  return {
    archModels,
    calculatedTreatmentType,
    scanNumber,
    initAlignerNumber,
    endAlignerNumber,
    alignerNumber,
    parsedTxTrackerData,
    isRetentionResult,
    retentionReferenceId: trackingStamp?.referenceId
  }
}
